/* eslint-disable @typescript-eslint/no-explicit-any */
import { globals } from "@/main";
import { defineComponent, ref, computed } from "vue";

export default defineComponent({
  name: "c-img",

  props: {
    src: { type: [Array, String], default: "" },
    style: { type: [String, Object], default: "" },
    class: { type: String, default: "" },
    keyValue: { type: String, default: "" },
    isDelete: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    isOnLoading: { type: Boolean, default: false },
    code: { type: String, default: "key" },
    isPath: { type: Boolean, default: true },
    isThumbnail: { type: Boolean, default: false },
    file: { type: Boolean, default: false },
    uploaded: { type: Number, default: 0 },
    uploadedIndex: { type: Number, default: 0 },
    thumbnail: { type: String, default: "" },
    error: { type: String, default: "" },
  },

  emits: ["update:src"],

  setup(props, { emit }) {
    const className = ref(props.class);
    const styleName = ref(props.style);
    const isError = computed(() => props.error);

    const imgList = computed(() => {
      if (typeof props.src === "string") {
        return [props.src];
      } else if (Array.isArray(props.src)) {
        return [...props.src];
      } else {
        return [];
      }
    });

    const onDeleteImage = (index: number): void => {
      let updatedSrc = [...imgList.value];

      if (index >= 0 && index < updatedSrc.length) {
        updatedSrc = updatedSrc.filter((_, i) => i !== index);
        emit("update:src", updatedSrc);
      }
    };

    const isLoading = ref(true);

    const onImageLoad = async () => {
      if (!props.isOnLoading) {
        setTimeout(() => {
          isLoading.value = false;
        }, 1000);
      }
    };

    const errorSrc = ref("");

    const onImageError = (event: Event) => {
      isLoading.value = false;
      errorSrc.value = require("@/assets/default.svg");
      return event;
    };

    const getValue = (value: any) => {
      if (!props.isPath) {
        return value;
      }

      if (isFile(value)) {
        if (isValidImageFile(value)) {
          return fileToPath(value);
        }
      }

      if (value.startsWith("blob")) {
        return value;
      }

      if (typeof value === "string") {
        return globals.$helper?.getFullImage(value);
      }

      if (Array.isArray(value)) {
        if (value.length > 0 && typeof value[0] === "object") {
          return value[0];
        } else {
          return globals.$helper?.getFullImage(value);
        }
      }

      if (typeof value === "object" && value !== null) {
        if (props.code && value[props.code]) {
          return globals.$helper?.getFullImage(value[props.code]);
        }
      }

      return globals.$helper?.getFullImage(value);
    };

    const fileToPath = (file: File): any => {
      return window.URL.createObjectURL(file);
    };

    const isValidImageFile = (file: File): boolean => {
      const validImageTypes = [
        "image/jpeg",
        "image/jpg",
        "image/svg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      const validExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];

      // Check MIME type
      const isValidMimeType = validImageTypes.includes(file.type);

      // Check file extension
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      const isValidExtension = fileExtension
        ? validExtensions.includes(fileExtension)
        : false;

      return isValidMimeType || isValidExtension;
    };

    const isFile = (value: any): value is File => {
      return value instanceof File;
    };

    const spinnerStyle = computed(() => ({
      animationDuration: `${2 - (props.uploaded / 100) * 1.8}s`,
    }));

    return {
      className,
      styleName,
      imgList,
      onDeleteImage,
      onImageLoad,
      isLoading,
      onImageError,
      errorSrc,
      getValue,
      isFile,
      spinnerStyle,
      isError,
    };
  },
});
